import { useDataGridContext } from '@cmg/data-grid';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  ListItem,
  ListItemText,
  SnackbarManager,
} from '@cmg/design-system';
import { FormikAsyncAutocompleteField } from '@cmg/design-system-formik';
import type { FormikConfig } from 'formik';
import { FormikProvider, useFormik } from 'formik';
import defaultTo from 'lodash/defaultTo';
import isEqual from 'lodash/isEqual';
import { useCallback, useState } from 'react';

import { RelationshipType, RolodexRecordStatus } from '../../../../../../../graphql';
import { canEmployeeEditCoverage } from '../InvestorCoverageGrid/InvestorCoverageGrid.model';
import { useAddSalesCoverageCreateRelationship } from './hooks/useAddSalesCoverageCreateRelationship';
import { useAddSalesCoverageFirmSearch } from './hooks/useAddSalesCoverageFirmSearch';

export type AddSalesCoverageProps = Readonly<{
  tenantId?: string;
  employeeId: string;
  employeeStatus: RolodexRecordStatus;
}>;

type InvestorFirmSearchOption = {
  label: string;
  value: string;
  firmKey: string | null | undefined;
};

const PAGE_SIZE = 25;

export function AddSalesCoverage({ tenantId, employeeId, employeeStatus }: AddSalesCoverageProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { refreshServerSide } = useDataGridContext();

  const createRelationships = useAddSalesCoverageCreateRelationship(tenantId);

  const { loadFirms, loading } = useAddSalesCoverageFirmSearch(tenantId);

  const handleFetchOptions = useCallback(
    async (searchText: string, loadedPage: number = 1) => {
      const { data } = await loadFirms({
        searchText,
        underwriterEmployeeId: employeeId,
        skip: (loadedPage - 1) * PAGE_SIZE,
        take: PAGE_SIZE,
      });
      return {
        data: defaultTo(
          data?.investorFirmsGrid?.items?.map(({ id, firmKey, name }) => ({
            label: name,
            value: id,
            firmKey,
          })),
          []
        ),
        pagination: {
          hasNext: defaultTo(data?.investorFirmsGrid?.pageInfo?.hasNextPage, false),
        },
      };
    },
    [loadFirms, employeeId]
  );

  const handleOpenDialog = useCallback(() => {
    setIsDialogOpen(true);
  }, [setIsDialogOpen]);

  const handleCloseDialog = useCallback(() => {
    setIsDialogOpen(false);
  }, [setIsDialogOpen]);

  const handleSubmit = useCallback<
    FormikConfig<{ selectedFirms: InvestorFirmSearchOption[] }>['onSubmit']
  >(
    async (values, formikHelpers) => {
      try {
        await createRelationships(
          values.selectedFirms.map(firm => ({
            investorFirmId: firm.value,
            relationshipType: RelationshipType.Primary,
            status: RolodexRecordStatus.Effective,
            underwriterEmployeeId: employeeId,
          }))
        );
        handleCloseDialog();
        formikHelpers.resetForm();
        refreshServerSide();
        SnackbarManager.success('Sales coverage added successfully');
      } catch {
        SnackbarManager.error('Failed to add sales coverage');
      }
    },
    [handleCloseDialog, createRelationships, employeeId, refreshServerSide]
  );

  const formik = useFormik<{ selectedFirms: InvestorFirmSearchOption[] }>({
    initialValues: {
      selectedFirms: [],
    },
    onSubmit: handleSubmit,
  });

  const handleSave = useCallback(() => {
    formik.handleSubmit();
  }, [formik]);

  return (
    <FormikProvider value={formik}>
      <Button
        variant="outlined"
        onClick={handleOpenDialog}
        disabled={!canEmployeeEditCoverage(employeeStatus)}
      >
        Add Sales Coverage
      </Button>
      <Dialog open={isDialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>Add Sales Coverage</DialogTitle>
        <Divider />
        <DialogContent>
          <FormikAsyncAutocompleteField<InvestorFirmSearchOption, true, true>
            multiple
            name="selectedFirms"
            isOptionEqualToValue={isEqual}
            isLoadingOptions={loading}
            onFetchOptions={handleFetchOptions}
            label="Firms"
            required
            placeholder="Select Firms..."
            disableClearable
            renderOption={(props, option) => (
              <ListItem {...props}>
                <ListItemText primary={option.label} secondary={option.firmKey} />
              </ListItem>
            )}
          ></FormikAsyncAutocompleteField>
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button onClick={handleCloseDialog} variant="outlined" color="primary">
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </FormikProvider>
  );
}
